import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Trophy, Timer, TrendingUp } from 'lucide-react';

const BallJump = ({ onGameEnd }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('waiting');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [lives, setLives] = useState(3);
  const gameLoopRef = useRef(null);
  const ballRef = useRef({ y: 300, velocity: 0 });
  const platformsRef = useRef([]);
  const gameSpeedRef = useRef(2);

  useEffect(() => {
    if (gameState === 'playing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Initialize platforms
      if (platformsRef.current.length === 0) {
        const platforms = [];
        for (let i = 0; i < 8; i++) {
          platforms.push({
            y: height - (i + 1) * 100,
            x: Math.random() * (width - 100),
            width: 80 + Math.random() * 60,
            passed: false,
            color: `hsl(${Math.random() * 360}, 70%, 50%)`
          });
        }
        platformsRef.current = platforms;
      }

      const gravity = 0.6;
      const jumpPower = -12;

      const gameLoop = () => {
        // Clear canvas
        const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
        bgGradient.addColorStop(0, '#1e1b4b');
        bgGradient.addColorStop(1, '#0c0a20');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, width, height);

        // Draw stars background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        for (let i = 0; i < 50; i++) {
          const x = (i * 123) % width;
          const y = (i * 456 + Date.now() * 0.01) % height;
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fill();
        }

        // Update platforms
        platformsRef.current = platformsRef.current.map(platform => {
          platform.y += gameSpeedRef.current;
          return platform;
        });

        // Remove platforms that went off screen and add new ones at top
        if (platformsRef.current[0] && platformsRef.current[0].y > height + 50) {
          platformsRef.current.shift();
          const newY = platformsRef.current[platformsRef.current.length - 1].y - 100;
          platformsRef.current.push({
            y: newY,
            x: Math.random() * (width - 100),
            width: 80 + Math.random() * 60,
            passed: false,
            color: `hsl(${Math.random() * 360}, 70%, 50%)`
          });
        }

        // Draw platforms
        platformsRef.current.forEach(platform => {
          const gradient = ctx.createLinearGradient(
            platform.x, platform.y,
            platform.x + platform.width, platform.y + 15
          );
          gradient.addColorStop(0, platform.color);
          gradient.addColorStop(1, '#1a1a2e');
          
          ctx.fillStyle = gradient;
          ctx.fillRect(platform.x, platform.y, platform.width, 15);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.lineWidth = 2;
          ctx.strokeRect(platform.x, platform.y, platform.width, 15);
        });

        // Update ball
        ballRef.current.velocity += gravity;
        ballRef.current.y += ballRef.current.velocity;

        // Check collision with platforms
        platformsRef.current.forEach(platform => {
          const ballX = width / 2;
          const ballBottom = ballRef.current.y + 15;
          
          if (
            ballBottom >= platform.y &&
            ballBottom <= platform.y + 15 &&
            ballRef.current.velocity > 0 &&
            ballX >= platform.x &&
            ballX <= platform.x + platform.width
          ) {
            ballRef.current.velocity = jumpPower;
            
            if (!platform.passed) {
              platform.passed = true;
              setScore(prev => prev + 10);
            }
          }
        });

        // Check if ball fell
        if (ballRef.current.y > height) {
          setLives(prev => {
            const newLives = prev - 1;
            if (newLives <= 0) {
              setGameState('finished');
            }
            return newLives;
          });
          ballRef.current.y = 300;
          ballRef.current.velocity = 0;
        }

        // Draw ball
        const ballX = width / 2;
        const ballY = ballRef.current.y;
        
        // Ball shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.beginPath();
        ctx.ellipse(ballX, ballY + 20, 15, 5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Ball gradient
        const ballGradient = ctx.createRadialGradient(ballX - 5, ballY - 5, 3, ballX, ballY, 15);
        ballGradient.addColorStop(0, '#fcd34d');
        ballGradient.addColorStop(1, '#f59e0b');
        
        ctx.fillStyle = ballGradient;
        ctx.beginPath();
        ctx.arc(ballX, ballY, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#d97706';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw score and lives on canvas
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(10, 10, width - 20, 50);
        
        ctx.fillStyle = '#fbbf24';
        ctx.font = 'bold 20px Arial';
        ctx.fillText(`Score: ${score}`, 20, 40);
        
        ctx.fillStyle = '#ef4444';
        ctx.fillText(`❤️ x ${lives}`, width - 100, 40);

        // Increase difficulty
        gameSpeedRef.current = Math.min(2 + score / 100, 5);

        gameLoopRef.current = requestAnimationFrame(gameLoop);
      };

      gameLoop();

      return () => {
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      };
    }
  }, [gameState, score, lives]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (gameState === 'finished') {
      // Stop the game loop
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      // Call onGameEnd after a short delay to show the final screen
      setTimeout(() => {
        onGameEnd(score);
      }, 2000);
    }
  }, [gameState, score, onGameEnd]);

  const handleJump = () => {
    if (gameState === 'playing') {
      ballRef.current.velocity = -12;
    }
  };

  const handleKeyPress = (e) => {
    if (e.code === 'Space' && gameState === 'playing') {
      handleJump();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState]);

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setTimeLeft(60);
    setLives(3);
    ballRef.current = { y: 300, velocity: 0 };
    platformsRef.current = [];
    gameSpeedRef.current = 2;
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg p-6 border border-indigo-500/30">
        {/* Game Stats */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 text-yellow-400">
            <Trophy className="w-5 h-5" />
            <span className="font-bold text-xl">Score: {score}</span>
          </div>
          <div className="flex items-center gap-2 text-blue-400">
            <Timer className="w-5 h-5" />
            <span className="font-bold text-xl">{timeLeft}s</span>
          </div>
          <div className="flex items-center gap-2 text-red-400">
            <span className="text-2xl">❤️</span>
            <span className="font-bold text-xl">x {lives}</span>
          </div>
        </div>

        {/* Game Canvas */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={400}
            height={600}
            className="w-full h-auto"
            onClick={handleJump}
          />
          
          {gameState === 'waiting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <TrendingUp className="w-16 h-16 text-yellow-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Ball Jump Challenge</h3>
              <p className="text-gray-300 text-center mb-4 max-w-md px-4">
                Jump from platform to platform! Don't let the ball fall.
                Each platform gives you 10 points. You have 3 lives!
              </p>
              <Button onClick={startGame} className="gaming-button">
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'finished' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
              <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
              <h3 className="text-3xl font-bold text-white mb-2">Game Over!</h3>
              <p className="text-2xl text-yellow-400 font-bold mb-4">Final Score: {score}</p>
              <p className="text-gray-300 mb-6">Submitting your score...</p>
            </div>
          )}
        </div>

        {/* Jump Button */}
        <Button
          onClick={handleJump}
          disabled={gameState !== 'playing'}
          className="gaming-button w-full h-16 text-xl font-bold"
        >
          TAP TO JUMP
        </Button>

        {/* Instructions */}
        <div className="mt-4 text-sm text-gray-400 text-center">
          <p>🎮 Click/Tap or press SPACE to jump</p>
          <p>⬆️ Land on platforms to score points</p>
          <p>💀 Game speeds up as you score more!</p>
        </div>
      </div>
    </div>
  );
};

export default BallJump;
